import _extends from "@babel/runtime/helpers/extends";
import _typeof from "@babel/runtime/helpers/typeof";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import StarRatings from 'react-star-ratings';
import { analyticsTrack } from '../../analyticsUtils';
import { getRequest, putRequest } from '../../requestUtils';
import Loading from '../common/Loading';
import ProposalStatusPill from '../common/ProposalStatusPill';
import MoreActions from '../common/ProposalShow/MoreActions';
import WaitlistedNotification from '../common/WaitlistedNotification';
import ShareResource from '../ShareResource';
import InterviewTimeRange from '../common/InterviewSubComponents/InterviewTimeRange';
import InterviewTimezone from '../common/InterviewSubComponents/InterviewTimezone';
import { ordinalize } from '../../numberUtils';
import rankedTagImg from '../../../assets/images/icons/ranked_tag.svg';
import userMapIcon from '../../../assets/images/icons/map-pin.png';
import experienceIcon from '../../../assets/images/icons/experience.svg';
import profileIcon from '../../../assets/images/profile-user.png';
import cloraIcon from '../../../assets/images/onboarding-state-active.svg';
import './ProjectProposalReviewTab.scss';
var ConfirmModal = /*#__PURE__*/lazy(function () {
  return import('../ConfirmModal');
});
var ScheduleInterviewModal = /*#__PURE__*/lazy(function () {
  return import('../ScheduleInterviewModal');
});
var DeclineProposal = /*#__PURE__*/lazy(function () {
  return import('../DeclineProposal');
});
var WithdrawProposal = /*#__PURE__*/lazy(function () {
  return import('../common/WithdrawProposal');
});
var TransferApplication = /*#__PURE__*/lazy(function () {
  return import('../common/TransferApplication');
});
var AddCoworkersModal = /*#__PURE__*/lazy(function () {
  return import('../AddCoworkersModal');
});
var ProposalQuestion = /*#__PURE__*/lazy(function () {
  return import('../common/ProposalQuestion');
});
var ProposalFeedbacks = /*#__PURE__*/lazy(function () {
  return import('../ProposalFeedbacks');
});
var titleize = function titleize(value) {
  return (value || '').toString().replace(/_/g, ' ').replace(/\b\w/g, function (_char) {
    return _char.toUpperCase();
  });
};

// Matches ProposalShow.jsx's imgTag helper, used for the same rating/location/experience icons.
var imgTag = function imgTag(icon, altText) {
  return /*#__PURE__*/React.createElement("img", {
    className: "me-2",
    src: icon,
    alt: altText
  });
};
var formatMoney = function formatMoney(value, employmentType) {
  if (!value) return 'Rate unavailable';
  var amount = Number(value).toLocaleString();
  return employmentType === 'full_time' ? "$".concat(amount) : "$".concat(amount, "/hr");
};
var interviewStatusLabels = {
  upcoming: 'booked',
  pending: 'requested',
  reschedule: 'reschedule requested',
  past: 'booked (past)'
};
var interviewStatusLabel = function interviewStatusLabel(status) {
  return interviewStatusLabels[status] || 'booked';
};
var compactValues = function compactValues(values) {
  return values.filter(function (value) {
    return value !== null && value !== undefined && value !== '';
  });
};
var restoreResumeScrollPosition = function restoreResumeScrollPosition(scrollPosition) {
  var detailEl = document.querySelector('.proposal-review-detail');
  if (detailEl) detailEl.scrollTop = scrollPosition.detailTop;
  window.scrollTo(scrollPosition.windowX, scrollPosition.windowY);
};
var preserveResumeScrollPosition = function preserveResumeScrollPosition(scrollPosition) {
  restoreResumeScrollPosition(scrollPosition);
  window.requestAnimationFrame(function () {
    return restoreResumeScrollPosition(scrollPosition);
  });
  window.setTimeout(function () {
    return restoreResumeScrollPosition(scrollPosition);
  }, 75);
  window.setTimeout(function () {
    return restoreResumeScrollPosition(scrollPosition);
  }, 300);
};
function ResponseIndicator(_ref) {
  var checked = _ref.checked;
  var state = checked || 'unanswered';
  var label = 'Unanswered';
  if (state === 'yes') label = 'Yes';
  if (state === 'no') label = 'No';
  if (state === 'bullet') label = '';
  return /*#__PURE__*/React.createElement("span", {
    className: "proposal-review-response-indicator proposal-review-response-indicator--".concat(state),
    "aria-label": label,
    title: label
  });
}
function ChipGroup(_ref2) {
  var values = _ref2.values;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    expanded = _useState2[0],
    setExpanded = _useState2[1];
  if (!values || values.length === 0) return /*#__PURE__*/React.createElement("span", {
    className: "proposal-review-muted"
  }, "None listed");
  var visible = expanded ? values : values.slice(0, 8);
  var hiddenCount = values.length - visible.length;
  return /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-chips"
  }, visible.map(function (value) {
    // Tag objects (functional/therapeutic areas) carry proficiency years and an optional
    // "top tag" ranking badge - see ProjectProposalReviewDetailSerializer#expertise_tags.
    // Other categories (skills/industries/tools/credentials) are still plain strings.
    var isTagObject = _typeof(value) === 'object' && value !== null;
    var name = isTagObject ? value.name : value;
    return /*#__PURE__*/React.createElement("span", {
      className: "proposal-review-chip",
      key: name
    }, isTagObject && value.ranked && /*#__PURE__*/React.createElement("img", {
      className: "proposal-review-chip-badge",
      src: rankedTagImg,
      alt: "Top tag",
      height: "12",
      width: "9"
    }), name, isTagObject && value.experience && /*#__PURE__*/React.createElement("span", {
      className: "proposal-review-chip-experience"
    }, " (".concat(value.experience, ")")));
  }), hiddenCount > 0 && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "proposal-review-link-btn",
    onClick: function onClick() {
      return setExpanded(true);
    }
  }, "+", hiddenCount, ' ', "more"), expanded && values.length > 8 && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "proposal-review-link-btn",
    onClick: function onClick() {
      return setExpanded(false);
    }
  }, "Show less"));
}
function ExpandableText(_ref3) {
  var text = _ref3.text,
    _ref3$emptyText = _ref3.emptyText,
    emptyText = _ref3$emptyText === void 0 ? 'No summary provided.' : _ref3$emptyText;
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    expanded = _useState4[0],
    setExpanded = _useState4[1];
  var content = text || emptyText;
  var isLong = content.length > 420;
  var displayed = !expanded && isLong ? "".concat(content.slice(0, 420), "...") : content;
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", {
    className: "proposal-review-body-text"
  }, displayed), isLong && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "proposal-review-link-btn",
    onClick: function onClick() {
      return setExpanded(!expanded);
    }
  }, expanded ? 'Show less' : 'Show more'));
}
function Section(_ref4) {
  var title = _ref4.title,
    titleLink = _ref4.titleLink,
    children = _ref4.children,
    headerAside = _ref4.headerAside;
  var isProposalInformation = title === 'Proposal Information';
  return /*#__PURE__*/React.createElement("section", {
    className: "proposal-review-section"
  }, /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-section-header".concat(isProposalInformation ? ' proposal-review-section-header--highlight' : '')
  }, /*#__PURE__*/React.createElement("h2", null, title, (titleLink === null || titleLink === void 0 ? void 0 : titleLink.href) && /*#__PURE__*/React.createElement("a", {
    href: titleLink.href,
    target: "_blank",
    rel: "noreferrer"
  }, titleLink.label)), headerAside), children && /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-section-body"
  }, children));
}
function ProposalDetailSections(_ref5) {
  var sections = _ref5.sections;
  if (!sections || sections.length === 0) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-proposal-sections"
  }, sections.map(function (section) {
    return /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-proposal-subsection",
      key: section.title
    }, /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-info-row proposal-review-info-row--heading"
    }, /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-info-label"
    }, /*#__PURE__*/React.createElement("strong", null, section.title)), /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-info-value"
    }, section.items && section.items.length > 0 ? section.items.map(function (item) {
      return /*#__PURE__*/React.createElement("div", {
        className: "proposal-review-response-line",
        key: "".concat(section.title, "-").concat(item.label)
      }, item.checked && /*#__PURE__*/React.createElement(ResponseIndicator, {
        checked: item.checked
      }), /*#__PURE__*/React.createElement("span", null, item.label));
    }) : /*#__PURE__*/React.createElement("span", {
      className: "proposal-review-muted"
    }, "No response provided."))), section.comments && /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-info-row"
    }, /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-info-label"
    }, /*#__PURE__*/React.createElement("strong", null, section.comments.label)), /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-info-value"
    }, section.comments.value)));
  }));
}
var renderProposalDetailItems = function renderProposalDetailItems(section) {
  return /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-info-value"
  }, section.items && section.items.length > 0 ? section.items.map(function (item) {
    return /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-response-line",
      key: "".concat(section.title, "-").concat(item.label)
    }, item.checked && /*#__PURE__*/React.createElement(ResponseIndicator, {
      checked: item.checked
    }), /*#__PURE__*/React.createElement("span", null, item.label));
  }) : /*#__PURE__*/React.createElement("span", {
    className: "proposal-review-muted"
  }, "No response provided."), section.comments && /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-info-row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-info-label"
  }, /*#__PURE__*/React.createElement("strong", null, section.comments.label)), /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-info-value"
  }, section.comments.value)));
};
var renderInternalFeedback = function renderInternalFeedback(feedbacks) {
  if (!feedbacks || feedbacks.length === 0) {
    return /*#__PURE__*/React.createElement("p", {
      className: "proposal-review-muted"
    }, "No internal feedback provided.");
  }
  return feedbacks.map(function (feedback) {
    return /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-feedback",
      key: "".concat(feedback.author, "-").concat(feedback.updated_at)
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("strong", null, feedback.author ? "User: ".concat(feedback.author) : 'Feedback'), feedback.updated_at && /*#__PURE__*/React.createElement("span", null, feedback.updated_at)), feedback.consultant_fit && /*#__PURE__*/React.createElement("span", {
      className: "proposal-review-feedback-fit"
    }, titleize(feedback.consultant_fit)), (feedback.lines || []).map(function (line) {
      return /*#__PURE__*/React.createElement("p", {
        key: line
      }, line);
    }));
  });
};
var renderProfileExpertices = function renderProfileExpertices(expertise) {
  var profileExpertise = Object.entries(expertise).filter(function (_ref6) {
    var _ref7 = _slicedToArray(_ref6, 1),
      label = _ref7[0];
    return label !== 'credentials';
  });
  if (profileExpertise.length === 0) {
    return /*#__PURE__*/React.createElement("p", {
      className: "proposal-review-muted"
    }, "No expertise listed.");
  }
  return profileExpertise.map(function (_ref8) {
    var _ref9 = _slicedToArray(_ref8, 2),
      label = _ref9[0],
      values = _ref9[1];
    return /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-info-row",
      key: label
    }, /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-info-label"
    }, /*#__PURE__*/React.createElement("strong", null, titleize(label))), /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-info-value"
    }, /*#__PURE__*/React.createElement(ChipGroup, {
      values: values || []
    })));
  });
};
var renderPastRoles = function renderPastRoles(roles) {
  if (!roles || roles.length === 0) {
    return /*#__PURE__*/React.createElement("p", {
      className: "proposal-review-muted"
    }, "No past roles listed.");
  }
  return roles.map(function (role) {
    return /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-role",
      key: "".concat(role.company, "-").concat(role.title, "-").concat(role.dates)
    }, /*#__PURE__*/React.createElement("strong", null, role.title || 'Role title unavailable'), /*#__PURE__*/React.createElement("span", null, compactValues([role.company, role.dates]).join(' | ')));
  });
};
var renderWorkPreference = function renderWorkPreference(header, employmentType) {
  return /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-info-value"
  }, /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-response-line"
  }, /*#__PURE__*/React.createElement("strong", null, "Rate"), /*#__PURE__*/React.createElement("span", null, formatMoney(header.proposed_rate, employmentType))), header.availability && /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-response-line"
  }, /*#__PURE__*/React.createElement("strong", null, "Availability"), /*#__PURE__*/React.createElement("span", null, header.availability)));
};
function CandidateList(_ref10) {
  var candidates = _ref10.candidates,
    allCandidates = _ref10.allCandidates,
    selectedCandidateKey = _ref10.selectedCandidateKey,
    onSelect = _ref10.onSelect,
    employmentType = _ref10.employmentType,
    query = _ref10.query,
    setQuery = _ref10.setQuery,
    status = _ref10.status,
    setStatus = _ref10.setStatus;
  var statuses = useMemo(function () {
    return Array.from(new Set(allCandidates.map(function (candidate) {
      return candidate.status;
    }).filter(Boolean)));
  }, [allCandidates]);
  return /*#__PURE__*/React.createElement("aside", {
    className: "proposal-review-list col-12 col-xl-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-pane-card"
  }, /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-list-header"
  }, /*#__PURE__*/React.createElement("h2", null, "Proposal Review"), /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-controls"
  }, /*#__PURE__*/React.createElement("input", {
    type: "search",
    value: query,
    onChange: function onChange(event) {
      return setQuery(event.target.value);
    },
    placeholder: "Search candidates",
    "aria-label": "Search candidates"
  }), /*#__PURE__*/React.createElement("select", {
    value: status,
    onChange: function onChange(event) {
      return setStatus(event.target.value);
    },
    "aria-label": "Filter by status"
  }, /*#__PURE__*/React.createElement("option", {
    value: ""
  }, "All statuses"), statuses.map(function (value) {
    return /*#__PURE__*/React.createElement("option", {
      value: value,
      key: value
    }, titleize(value));
  })), /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-list-caption"
  }, /*#__PURE__*/React.createElement("strong", null, "Proposals submitted"), /*#__PURE__*/React.createElement("span", null, candidates.length, ' ', candidates.length === 1 ? 'candidate' : 'candidates')))), /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-candidates"
  }, candidates.length === 0 && /*#__PURE__*/React.createElement("p", {
    className: "proposal-review-empty"
  }, "No candidates found."), candidates.map(function (candidate) {
    var candidateKey = "".concat(candidate.candidate_type, ":").concat(candidate.id);
    var selected = candidateKey === selectedCandidateKey;
    return /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "proposal-review-candidate proposal-review-candidate--selected-".concat(selected),
      key: candidateKey,
      onClick: function onClick() {
        return onSelect(candidate);
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "proposal-review-candidate-main"
    }, /*#__PURE__*/React.createElement("strong", null, candidate.consultant_name || 'Candidate'), /*#__PURE__*/React.createElement("span", null, formatMoney(candidate.amount, employmentType))), /*#__PURE__*/React.createElement("span", {
      className: "proposal-review-candidate-meta"
    }, /*#__PURE__*/React.createElement(ProposalStatusPill, {
      status: candidate.status
    }), /*#__PURE__*/React.createElement("span", null, candidate.submitted_at || candidate.last_updated)));
  }))));
}
function DetailHeader(_ref11) {
  var detail = _ref11.detail,
    onBack = _ref11.onBack,
    onPrevious = _ref11.onPrevious,
    onNext = _ref11.onNext,
    hasPrevious = _ref11.hasPrevious,
    hasNext = _ref11.hasNext,
    onWaitlist = _ref11.onWaitlist,
    onActionComplete = _ref11.onActionComplete,
    csrfToken = _ref11.csrfToken,
    cloraRep = _ref11.cloraRep,
    initialTeamMembers = _ref11.initialTeamMembers,
    invitableRoles = _ref11.invitableRoles,
    createInterviewUrl = _ref11.createInterviewUrl,
    availableInterviewSlotUrl = _ref11.availableInterviewSlotUrl,
    hiddenFeedbackOptions = _ref11.hiddenFeedbackOptions,
    visibleFeedbackOptions = _ref11.visibleFeedbackOptions,
    consultantFeedbackOptions = _ref11.consultantFeedbackOptions,
    projectsOptions = _ref11.projectsOptions,
    projectResourceId = _ref11.projectResourceId,
    sourceOptions = _ref11.sourceOptions,
    recipientOptions = _ref11.recipientOptions,
    basePermissionsUrl = _ref11.basePermissionsUrl,
    editableRoles = _ref11.editableRoles,
    currentUserEmail = _ref11.currentUserEmail;
  var header = detail.header;
  var isProposal = detail.candidate_type === 'proposal';
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    showAddCoworkerModal = _useState6[0],
    setShowAddCoworkerModal = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    showAskQuestionModal = _useState8[0],
    setShowAskQuestionModal = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    showProposalFeedbackModal = _useState10[0],
    setShowProposalFeedbackModal = _useState10[1];

  // Mirrors ProposalShow.jsx's getButtonActions(): the first primary action that isn't in this
  // skip list gets the solid/primary style, everything else (including Decline) gets the
  // outline style - matching the button design on the full proposal page.
  var skipPrimaryStyleLabels = ['Decline', 'Share with a Colleague', 'View Other Opportunities'];
  var primaryStyleClaimed = false;
  var buttonStyleFor = function buttonStyleFor(label) {
    if (!primaryStyleClaimed && !skipPrimaryStyleLabels.includes(label)) {
      primaryStyleClaimed = true;
      return 'btn--green-darkest';
    }
    return 'btn--border-darkest';
  };

  // Waitlist and Decline update this tab's own state in place (no navigation). Interview requests
  // reuse ScheduleInterviewModal as-is, which - unlike the other action components -
  // always does a window.location reload on success rather than calling a callback. Since the
  // URL here already encodes the selected candidate (see selectCandidate's history.pushState),
  // that reload lands back on this same tab/candidate instead of the separate proposal page,
  // which is what actually mattered for the "takes me to the proposal page" complaint - it's
  // just not a zero-reload update like Waitlist/Decline.
  var renderPrimaryAction = function renderPrimaryAction(label) {
    var buttonStyle = buttonStyleFor(label);
    if (!isProposal) {
      return /*#__PURE__*/React.createElement("a", {
        href: header.proposal_url,
        className: "btn ".concat(buttonStyle),
        key: label
      }, label);
    }
    if (label === 'Share with a Colleague') {
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "btn ".concat(buttonStyle),
        key: label,
        onClick: function onClick() {
          return setShowAddCoworkerModal(true);
        }
      }, label);
    }
    if (label === 'Waitlist') {
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "btn ".concat(buttonStyle),
        key: label,
        onClick: onWaitlist
      }, label);
    }
    if (label === 'Hire') {
      return /*#__PURE__*/React.createElement(Suspense, {
        fallback: null,
        key: label
      }, /*#__PURE__*/React.createElement(ConfirmModal, {
        submitUrl: header.accept_proposal_url,
        proposalResourceId: detail.id,
        csrfToken: csrfToken,
        consultantName: header.consultant_name,
        possibleInterviewers: initialTeamMembers,
        invitableRoles: invitableRoles,
        type: "hire",
        reasonOptions: hiddenFeedbackOptions,
        modalTriggerType: "button",
        buttonType: buttonStyle,
        modalTriggerCopy: label,
        handleProposalAfterSubmit: onActionComplete
      }));
    }
    if (label === 'Decline') {
      return /*#__PURE__*/React.createElement(Suspense, {
        fallback: null,
        key: label
      }, /*#__PURE__*/React.createElement(DeclineProposal, {
        formUrl: header.reject_proposal_url,
        csrfToken: csrfToken,
        consultantName: header.consultant_name,
        triggerType: "button"
        // ModalTrigger renders a react-bootstrap Button with variant={buttonStyles}, which
        // prefixes it with "btn-", so the leading dash here reproduces the literal
        // "btn--border-darkest" class ProposalShow.jsx's own Decline button renders -
        // see ProposalShow.jsx's renderDeclineProposal, which does the same substring trick.
        ,
        buttonStyles: "".concat(buttonStyle.substring(4)),
        userType: cloraRep ? 'clora_rep' : 'employer',
        feedbackOptions: visibleFeedbackOptions,
        handleProposalAfterSubmit: onActionComplete,
        showNewProposalUX: true
      }));
    }
    if (label === 'Schedule Interview') {
      return /*#__PURE__*/React.createElement(Suspense, {
        fallback: null,
        key: label
      }, /*#__PURE__*/React.createElement(ConfirmModal, {
        submitUrl: createInterviewUrl,
        proposalResourceId: detail.id,
        csrfToken: csrfToken,
        consultantName: header.consultant_name,
        possibleInterviewers: initialTeamMembers,
        invitableRoles: invitableRoles,
        type: "interview",
        reasonOptions: hiddenFeedbackOptions,
        modalTriggerType: "button",
        buttonType: buttonStyle,
        modalTriggerCopy: label,
        availableInterviewSlotUrl: availableInterviewSlotUrl,
        handleProposalAfterSubmit: onActionComplete
      }));
    }
    if (label === 'Edit') {
      return /*#__PURE__*/React.createElement("a", {
        href: header.edit_proposal_url,
        className: "btn ".concat(buttonStyle),
        key: label
      }, label);
    }
    if (label === 'Request Interview' || label === 'Schedule Another Interview') {
      var interviewTriggerCopy = label === 'Schedule Another Interview' && !cloraRep ? 'Request Another Interview' : label;
      return /*#__PURE__*/React.createElement(Suspense, {
        fallback: null,
        key: label
      }, /*#__PURE__*/React.createElement(ScheduleInterviewModal, {
        createInterviewUrl: createInterviewUrl,
        proposalResourceId: detail.id,
        csrfToken: csrfToken,
        consultantName: header.consultant_name,
        possibleInterviewers: initialTeamMembers,
        invitableRoles: invitableRoles,
        modalTriggerType: "button",
        modalTriggerCopy: interviewTriggerCopy,
        modalTriggerClassName: buttonStyle,
        availableInterviewSlotUrl: availableInterviewSlotUrl
      }));
    }
    if (label === 'Leave Feedback' || label === 'Provide feedback') {
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "btn ".concat(buttonStyle),
        key: label,
        onClick: function onClick() {
          return setShowProposalFeedbackModal(true);
        }
      }, label);
    }
    if (label === 'Proposal Question') {
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "btn ".concat(buttonStyle),
        key: label,
        onClick: function onClick() {
          return setShowAskQuestionModal(true);
        }
      }, label);
    }

    // Other actions (Hire, Submit to Employer, Edit, etc.) still link out to the full
    // proposal page - each needs its own dedicated modal/flow not yet wired into this tab.
    return /*#__PURE__*/React.createElement("a", {
      href: header.proposal_url,
      className: "btn ".concat(buttonStyle),
      key: label
    }, label);
  };
  var renderHireAction = function renderHireAction() {
    var triggerType = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'link';
    return /*#__PURE__*/React.createElement(Suspense, {
      fallback: null,
      key: "Hire"
    }, /*#__PURE__*/React.createElement(ConfirmModal, {
      submitUrl: header.accept_proposal_url,
      proposalResourceId: detail.id,
      csrfToken: csrfToken,
      consultantName: header.consultant_name,
      possibleInterviewers: initialTeamMembers,
      invitableRoles: invitableRoles,
      type: "hire",
      reasonOptions: hiddenFeedbackOptions,
      modalTriggerType: triggerType,
      buttonType: "btn--green-darkest",
      modalTriggerCopy: "Hire",
      handleProposalAfterSubmit: onActionComplete
    }));
  };
  var newTabLabels = ['Open resume'];
  var renderSecondaryAction = function renderSecondaryAction(action) {
    if (action.label === 'Hire') {
      return renderHireAction();
    }
    if (action.label === 'Share Proposal') {
      if (!header.generate_shareable_link_url) return null;
      return /*#__PURE__*/React.createElement(Suspense, {
        fallback: null,
        key: action.label
      }, /*#__PURE__*/React.createElement(ShareResource, {
        generateUrl: header.generate_shareable_link_url,
        csrfToken: csrfToken,
        resourceId: detail.id,
        triggerText: "Share Proposal",
        triggerType: "link",
        buttonStyles: "link",
        idKey: "share-proposal-modal",
        resourceType: "Proposal",
        sourceOptions: sourceOptions,
        recipientOptions: recipientOptions,
        forceModal: true,
        stopPropagation: true
      }));
    }
    if (action.label === 'Share with a Colleague') {
      return /*#__PURE__*/React.createElement("a", {
        role: "button",
        tabIndex: 0,
        key: action.label,
        onClick: function onClick() {
          return setShowAddCoworkerModal(true);
        }
      }, action.label);
    }
    if (action.label === 'Withdraw') {
      return /*#__PURE__*/React.createElement(Suspense, {
        fallback: null,
        key: action.label
      }, /*#__PURE__*/React.createElement(WithdrawProposal, {
        formUrl: header.withdraw_url,
        csrfToken: csrfToken,
        consultantName: header.consultant_name,
        triggerType: "link",
        userType: "clora_rep",
        feedbackOptions: consultantFeedbackOptions,
        handleProposalAfterSubmit: onActionComplete,
        showNewProposalUX: true
      }));
    }
    if (action.label === 'Leave Feedback' || action.label === 'Provide feedback') {
      return /*#__PURE__*/React.createElement("a", {
        role: "button",
        tabIndex: 0,
        key: action.label,
        onClick: function onClick() {
          return setShowProposalFeedbackModal(true);
        }
      }, action.label);
    }
    if (action.label === 'Proposal Question') {
      return /*#__PURE__*/React.createElement("a", {
        role: "button",
        tabIndex: 0,
        key: action.label,
        onClick: function onClick() {
          return setShowAskQuestionModal(true);
        }
      }, action.label);
    }
    if (action.label === 'Move to Another Project') {
      var applicationProject = projectsOptions.find(function (project) {
        return project.value === projectResourceId;
      });
      var filteredProjects = projectsOptions.filter(function (project) {
        return project.value !== projectResourceId;
      });
      return /*#__PURE__*/React.createElement(Suspense, {
        fallback: null,
        key: action.label
      }, /*#__PURE__*/React.createElement(TransferApplication, {
        triggerText: "Move to another project",
        triggerType: "link",
        idKey: "move-application-modal",
        selectOptions: filteredProjects,
        csrfToken: csrfToken,
        proposaltResourceId: detail.id,
        applicationProject: applicationProject
      }));
    }
    var isNewTabLink = newTabLabels.includes(action.label);
    return /*#__PURE__*/React.createElement("a", _extends({
      href: action.url,
      key: "".concat(action.label, "-").concat(action.url)
    }, isNewTabLink ? {
      target: '_blank',
      rel: 'noreferrer'
    } : {}, action.method ? {
      'data-turbo-method': action.method,
      rel: 'nofollow'
    } : {}, action.confirm ? {
      'data-turbo-confirm': action.confirm
    } : {}), action.label);
  };

  // "Open resume" is redundant here - the resume section already has its own Open link.
  var visibleSecondaryActions = detail.secondary_actions.filter(function (action) {
    return action.label !== 'Open resume';
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-detail-header"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "proposal-review-back",
    onClick: onBack
  }, "Back to proposals"), /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-title-row"
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h2", null, header.consultant_name || 'Candidate'), header.status && /*#__PURE__*/React.createElement("p", {
    className: "proposal-review-meta-status"
  }, /*#__PURE__*/React.createElement(ProposalStatusPill, {
    status: header.status
  })), /*#__PURE__*/React.createElement("span", {
    className: "proposal-review-header-details"
  }, header.overall_rating && /*#__PURE__*/React.createElement("span", {
    className: "proposal-review-chip proposal-review-header-rating"
  }, /*#__PURE__*/React.createElement(StarRatings, {
    rating: header.overall_rating,
    starRatedColor: "#EDA318",
    starHoverColor: "#EDA318",
    starDimension: "18px",
    starSpacing: "1px",
    numberOfStars: 5,
    name: "rating_avg"
  }), /*#__PURE__*/React.createElement("span", {
    className: "ms-2"
  }, header.overall_rating)), header.location && /*#__PURE__*/React.createElement("span", {
    className: "proposal-review-chip proposal-review-header-location"
  }, imgTag(userMapIcon, 'location-icon'), header.location), header.years_of_experience && /*#__PURE__*/React.createElement("span", {
    className: "proposal-review-chip proposal-review-header-experience"
  }, imgTag(experienceIcon, 'experience-icon'), header.years_of_experience, ' ', "years experience"), cloraRep && /*#__PURE__*/React.createElement(React.Fragment, null, header.profile_url && /*#__PURE__*/React.createElement("a", {
    href: header.profile_url,
    className: "proposal-review-chip proposal-review-header-link",
    target: "_blank",
    rel: "noreferrer"
  }, imgTag(profileIcon, 'profile-icon'), "View Profile"), header.clorify_url && /*#__PURE__*/React.createElement("a", {
    href: header.clorify_url,
    className: "proposal-review-chip proposal-review-header-link",
    target: "_blank",
    rel: "noreferrer"
  }, imgTag(cloraIcon, 'clora-icon'), "View Clorify")))), /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-header-actions"
  }, detail.primary_actions.map(function (label) {
    return renderPrimaryAction(label);
  }), visibleSecondaryActions.length > 0 &&
  /*#__PURE__*/
  // MoreActions' dropdown styling only applies within this wrapper class -
  // see app/javascript/components/common/ProposalShow/MoreActions/MoreActions.scss
  React.createElement("span", {
    className: "new-proposal-ux"
  }, /*#__PURE__*/React.createElement(MoreActions, {
    listItems: visibleSecondaryActions.map(function (action) {
      return renderSecondaryAction(action);
    })
  })), isProposal && /*#__PURE__*/React.createElement(Suspense, {
    fallback: null
  }, /*#__PURE__*/React.createElement("span", {
    className: "proposal-review-hidden-trigger"
  }, /*#__PURE__*/React.createElement(AddCoworkersModal, {
    csrfToken: csrfToken,
    initialTeamMembers: initialTeamMembers,
    invitableRoles: invitableRoles,
    editableRoles: editableRoles,
    basePermissionsUrl: basePermissionsUrl,
    proposalResourceId: detail.id,
    consultantName: header.consultant_name,
    location: "proposal",
    modalIsOpen: showAddCoworkerModal,
    showModalCallback: function showModalCallback() {
      return setShowAddCoworkerModal(false);
    }
  }))), isProposal && header.proposal_question_url && /*#__PURE__*/React.createElement(Suspense, {
    fallback: null
  }, /*#__PURE__*/React.createElement("span", {
    className: "proposal-review-hidden-trigger"
  }, /*#__PURE__*/React.createElement(ProposalQuestion, {
    csrfToken: csrfToken,
    url: header.proposal_question_url,
    modalIsOpen: showAskQuestionModal,
    showModalCallback: function showModalCallback() {
      return setShowAskQuestionModal(false);
    }
  }))), isProposal && /*#__PURE__*/React.createElement(Suspense, {
    fallback: null
  }, /*#__PURE__*/React.createElement("span", {
    className: "proposal-review-hidden-trigger"
  }, /*#__PURE__*/React.createElement(ProposalFeedbacks, {
    canEditFeedback: detail.can_edit_feedback,
    candidateName: header.consultant_name,
    proposalResourceId: detail.id,
    id: detail.feedback && detail.feedback.id,
    feedbacks: detail.feedbacks || [],
    email: currentUserEmail,
    csrfToken: csrfToken,
    admin: cloraRep,
    consultantFit: detail.feedback && detail.feedback.consultant_fit,
    note: detail.feedback && detail.feedback.note,
    location: "proposal",
    handleShowCoworkerModal: function handleShowCoworkerModal() {
      return setShowAddCoworkerModal(true);
    },
    modalIsOpen: showProposalFeedbackModal,
    showModalCallback: function showModalCallback() {
      return setShowProposalFeedbackModal(false);
    },
    handleProposalAfterSubmit: onActionComplete,
    showNewProposalUX: true
  }))))), /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-prev-next"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "btn btn--green-darkest",
    onClick: onPrevious,
    disabled: !hasPrevious
  }, "Previous"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "btn btn--green-darkest",
    onClick: onNext,
    disabled: !hasNext
  }, "Next")));
}
function DetailPanel(_ref12) {
  var _detail$resume, _detail$professional_, _detail$relevant_expe, _detail$header;
  var detail = _ref12.detail,
    loading = _ref12.loading,
    onBack = _ref12.onBack,
    onPrevious = _ref12.onPrevious,
    onNext = _ref12.onNext,
    hasPrevious = _ref12.hasPrevious,
    hasNext = _ref12.hasNext,
    employmentType = _ref12.employmentType,
    onWaitlist = _ref12.onWaitlist,
    onActionComplete = _ref12.onActionComplete,
    csrfToken = _ref12.csrfToken,
    cloraRep = _ref12.cloraRep,
    initialTeamMembers = _ref12.initialTeamMembers,
    invitableRoles = _ref12.invitableRoles,
    createInterviewUrl = _ref12.createInterviewUrl,
    availableInterviewSlotUrl = _ref12.availableInterviewSlotUrl,
    hiddenFeedbackOptions = _ref12.hiddenFeedbackOptions,
    visibleFeedbackOptions = _ref12.visibleFeedbackOptions,
    consultantFeedbackOptions = _ref12.consultantFeedbackOptions,
    projectsOptions = _ref12.projectsOptions,
    projectResourceId = _ref12.projectResourceId,
    sourceOptions = _ref12.sourceOptions,
    recipientOptions = _ref12.recipientOptions,
    basePermissionsUrl = _ref12.basePermissionsUrl,
    editableRoles = _ref12.editableRoles,
    currentUserEmail = _ref12.currentUserEmail;
  var resumeScrollPosition = useRef({
    detailTop: 0,
    windowX: 0,
    windowY: 0
  });
  var captureResumeScrollPosition = function captureResumeScrollPosition() {
    var detailEl = document.querySelector('.proposal-review-detail');
    resumeScrollPosition.current = {
      detailTop: (detailEl === null || detailEl === void 0 ? void 0 : detailEl.scrollTop) || 0,
      windowX: window.scrollX,
      windowY: window.scrollY
    };
  };
  var handleResumePreviewLoad = function handleResumePreviewLoad(event) {
    event.currentTarget.blur();
    preserveResumeScrollPosition(resumeScrollPosition.current);
  };
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    resumeLoadError = _useState12[0],
    setResumeLoadError = _useState12[1];

  // Reset so a fresh candidate/resume gets a clean attempt instead of inheriting the previous
  // resume's error state.
  useEffect(function () {
    setResumeLoadError(false);
  }, [detail === null || detail === void 0 ? void 0 : (_detail$resume = detail.resume) === null || _detail$resume === void 0 ? void 0 : _detail$resume.preview_url]);
  var handleResumePreviewError = function handleResumePreviewError() {
    setResumeLoadError(true);
  };
  if (loading) {
    return /*#__PURE__*/React.createElement("main", {
      className: "proposal-review-detail proposal-review-detail--empty col-12 col-xl-8"
    }, /*#__PURE__*/React.createElement(Loading, null));
  }
  if (!detail) {
    return /*#__PURE__*/React.createElement("main", {
      className: "proposal-review-detail proposal-review-detail--empty col-12 col-xl-8"
    }, /*#__PURE__*/React.createElement("p", null, "Select a candidate to review."));
  }
  var proposalInfo = detail.proposal_information || {};
  var expertise = detail.expertise || {};
  var proposalDetailSections = proposalInfo.detail_sections || [];
  var logisticsSection = proposalDetailSections.find(function (section) {
    return section.title === 'Proposal Details';
  });
  var educationSectionTitles = ['Education Requirements', 'Certifications'];
  var educationSections = proposalDetailSections.filter(function (section) {
    return educationSectionTitles.includes(section.title);
  });
  var expertiseSections = proposalDetailSections.filter(function (section) {
    return section.title !== 'Proposal Details' && !educationSectionTitles.includes(section.title);
  });
  return /*#__PURE__*/React.createElement("main", {
    className: "proposal-review-detail col-12 col-xl-8",
    onScroll: captureResumeScrollPosition
  }, /*#__PURE__*/React.createElement(DetailHeader, {
    detail: detail,
    onBack: onBack,
    onPrevious: onPrevious,
    onNext: onNext,
    hasPrevious: hasPrevious,
    hasNext: hasNext,
    onWaitlist: onWaitlist,
    onActionComplete: onActionComplete,
    csrfToken: csrfToken,
    cloraRep: cloraRep,
    initialTeamMembers: initialTeamMembers,
    invitableRoles: invitableRoles,
    createInterviewUrl: createInterviewUrl,
    availableInterviewSlotUrl: availableInterviewSlotUrl,
    hiddenFeedbackOptions: hiddenFeedbackOptions,
    visibleFeedbackOptions: visibleFeedbackOptions,
    consultantFeedbackOptions: consultantFeedbackOptions,
    projectsOptions: projectsOptions,
    projectResourceId: projectResourceId,
    sourceOptions: sourceOptions,
    recipientOptions: recipientOptions,
    basePermissionsUrl: basePermissionsUrl,
    editableRoles: editableRoles,
    currentUserEmail: currentUserEmail
  }), /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-detail-body"
  }, /*#__PURE__*/React.createElement(Section, {
    title: "Bio"
  }, /*#__PURE__*/React.createElement(ExpandableText, {
    text: (_detail$professional_ = detail.professional_summary) === null || _detail$professional_ === void 0 ? void 0 : _detail$professional_.text,
    emptyText: "No bio provided."
  })), /*#__PURE__*/React.createElement(Section, {
    title: "Profile Expertise"
  }, renderProfileExpertices(expertise)), /*#__PURE__*/React.createElement(Section, {
    title: "Past Roles"
  }, renderPastRoles((_detail$relevant_expe = detail.relevant_experience) === null || _detail$relevant_expe === void 0 ? void 0 : _detail$relevant_expe.roles)), /*#__PURE__*/React.createElement(Section, {
    title: "Work Preference"
  }, renderWorkPreference(detail.header || {}, employmentType)), /*#__PURE__*/React.createElement(Section, {
    title: "Credentials"
  }, /*#__PURE__*/React.createElement(ChipGroup, {
    values: expertise.credentials || []
  })), /*#__PURE__*/React.createElement(Section, {
    title: "Proposal Information",
    titleLink: {
      href: (_detail$header = detail.header) === null || _detail$header === void 0 ? void 0 : _detail$header.proposal_url,
      label: 'View Proposal'
    },
    headerAside: /*#__PURE__*/React.createElement("span", {
      className: "proposal-review-header-submitted-date"
    }, proposalInfo.submission_date ? "Submitted on ".concat(proposalInfo.submission_date) : 'Not submitted')
  }), /*#__PURE__*/React.createElement(Section, {
    title: "Clora Remark"
  }, /*#__PURE__*/React.createElement(ExpandableText, {
    text: detail.advisor_remark,
    emptyText: "No Clora remark provided."
  })), cloraRep && /*#__PURE__*/React.createElement(Section, {
    title: "Internal feedback"
  }, renderInternalFeedback(detail.internal_feedback)), /*#__PURE__*/React.createElement(Section, {
    title: "About"
  }, /*#__PURE__*/React.createElement(ExpandableText, {
    text: proposalInfo.cover_message,
    emptyText: "No cover message provided."
  })), logisticsSection && /*#__PURE__*/React.createElement(Section, {
    title: "Logistics"
  }, renderProposalDetailItems(logisticsSection)), /*#__PURE__*/React.createElement(Section, {
    title: "Expertise"
  }, /*#__PURE__*/React.createElement(ProposalDetailSections, {
    sections: expertiseSections
  })), /*#__PURE__*/React.createElement(Section, {
    title: "Education"
  }, educationSections.length > 0 ? /*#__PURE__*/React.createElement(ProposalDetailSections, {
    sections: educationSections
  }) : /*#__PURE__*/React.createElement("p", {
    className: "proposal-review-muted"
  }, "No education requirements provided.")), detail.booked_interview && /*#__PURE__*/React.createElement(Section, {
    title: "Interviews"
  }, /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-interview-booked"
  }, /*#__PURE__*/React.createElement("strong", null, ordinalize(detail.booked_interview.round_number), ' ', "round interview", ' ', interviewStatusLabel(detail.booked_interview.status)), detail.booked_interview.accepted_time ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InterviewTimeRange, {
    interviewTime: detail.booked_interview.accepted_time,
    containerClassName: "proposal-review-interview-booked-time",
    opts: {
      timeZone: 'UTC',
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-interview-booked-timezone"
  }, /*#__PURE__*/React.createElement(InterviewTimezone, {
    timezoneStr: detail.booked_interview.timezone,
    dateStr: detail.booked_interview.accepted_time.date
  }))) : /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-interview-booked-timezone"
  }, "Awaiting the candidate to select a time."))), /*#__PURE__*/React.createElement(Section, {
    title: "Screening Question"
  }, detail.screening_responses.length === 0 ? /*#__PURE__*/React.createElement("p", {
    className: "proposal-review-muted"
  }, "No screening responses submitted.") : detail.screening_responses.map(function (response) {
    return /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-info-row",
      key: "".concat(response.question, "-").concat(response.answer)
    }, /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-info-label"
    }, /*#__PURE__*/React.createElement(ResponseIndicator, {
      checked: response.checked
    }), /*#__PURE__*/React.createElement("strong", null, response.question)), /*#__PURE__*/React.createElement("div", {
      className: "proposal-review-info-value"
    }, response.answer));
  })), /*#__PURE__*/React.createElement(Section, {
    title: "Submitted Resume"
  }, detail.resume ? /*#__PURE__*/React.createElement("div", {
    className: "proposal-review-resume"
  }, /*#__PURE__*/React.createElement("span", null, detail.resume.filename || 'Resume'), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("a", {
    href: detail.resume.view_url,
    target: "_blank",
    rel: "noreferrer"
  }, "Open"), /*#__PURE__*/React.createElement("a", {
    href: detail.resume.download_url,
    target: "_blank",
    rel: "noreferrer"
  }, "Download")), detail.resume.preview_url && (resumeLoadError ? /*#__PURE__*/React.createElement("div", {
    className: "pdf-fallback"
  }, "The resume could not be loaded.") : /*#__PURE__*/React.createElement("iframe", {
    src: detail.resume.preview_url,
    title: "Resume preview",
    loading: "lazy",
    tabIndex: "-1",
    onFocus: function onFocus(event) {
      return event.currentTarget.blur();
    },
    onMouseEnter: captureResumeScrollPosition,
    onLoad: handleResumePreviewLoad,
    onError: handleResumePreviewError
  }))) : /*#__PURE__*/React.createElement("p", {
    className: "proposal-review-muted"
  }, "No resume submitted."))));
}
function ProjectProposalReviewTab(_ref13) {
  var _detail$header2;
  var projectResourceId = _ref13.projectResourceId,
    csrfToken = _ref13.csrfToken,
    cloraRep = _ref13.cloraRep,
    initialTeamMembers = _ref13.initialTeamMembers,
    invitableRoles = _ref13.invitableRoles,
    createInterviewUrl = _ref13.createInterviewUrl,
    availableInterviewSlotUrl = _ref13.availableInterviewSlotUrl,
    hiddenFeedbackOptions = _ref13.hiddenFeedbackOptions,
    visibleFeedbackOptions = _ref13.visibleFeedbackOptions,
    consultantFeedbackOptions = _ref13.consultantFeedbackOptions,
    projectsOptions = _ref13.projectsOptions,
    sourceOptions = _ref13.sourceOptions,
    recipientOptions = _ref13.recipientOptions,
    basePermissionsUrl = _ref13.basePermissionsUrl,
    editableRoles = _ref13.editableRoles,
    currentUserEmail = _ref13.currentUserEmail;
  var _useState13 = useState([]),
    _useState14 = _slicedToArray(_useState13, 2),
    candidates = _useState14[0],
    setCandidates = _useState14[1];
  var _useState15 = useState('consulting'),
    _useState16 = _slicedToArray(_useState15, 2),
    employmentType = _useState16[0],
    setEmploymentType = _useState16[1];
  var _useState17 = useState(null),
    _useState18 = _slicedToArray(_useState17, 2),
    selectedCandidate = _useState18[0],
    setSelectedCandidate = _useState18[1];
  var _useState19 = useState(null),
    _useState20 = _slicedToArray(_useState19, 2),
    detail = _useState20[0],
    setDetail = _useState20[1];
  var _useState21 = useState(true),
    _useState22 = _slicedToArray(_useState21, 2),
    listLoading = _useState22[0],
    setListLoading = _useState22[1];
  var _useState23 = useState(false),
    _useState24 = _slicedToArray(_useState23, 2),
    detailLoading = _useState24[0],
    setDetailLoading = _useState24[1];
  var _useState25 = useState(''),
    _useState26 = _slicedToArray(_useState25, 2),
    query = _useState26[0],
    setQuery = _useState26[1];
  var _useState27 = useState(''),
    _useState28 = _slicedToArray(_useState27, 2),
    status = _useState28[0],
    setStatus = _useState28[1];
  var _useState29 = useState(false),
    _useState30 = _slicedToArray(_useState29, 2),
    mobileDetailOpen = _useState30[0],
    setMobileDetailOpen = _useState30[1];
  var _useState31 = useState(false),
    _useState32 = _slicedToArray(_useState31, 2),
    showWaitlistModal = _useState32[0],
    setShowWaitlistModal = _useState32[1];
  var fetchList = function fetchList() {
    return getRequest("/projects/".concat(projectResourceId, "/proposal_review")).then(function (_ref14) {
      var data = _ref14.data;
      setCandidates(data.candidates || []);
      setEmploymentType(data.employment_type || 'consulting');
      setListLoading(false);
      analyticsTrack('Viewed proposal review tab', {
        location: 'Projects view'
      });
    })["catch"](function () {
      setListLoading(false);
    });
  };
  useEffect(function () {
    fetchList();
  }, [projectResourceId, csrfToken]);
  var filteredCandidates = useMemo(function () {
    return candidates.filter(function (candidate) {
      var nameMatches = (candidate.consultant_name || '').toLowerCase().includes(query.toLowerCase());
      var statusMatches = !status || candidate.status === status;
      return nameMatches && statusMatches;
    });
  }, [candidates, query, status]);
  var selectedCandidateKey = selectedCandidate ? "".concat(selectedCandidate.candidate_type, ":").concat(selectedCandidate.id) : null;
  var selectedIndex = filteredCandidates.findIndex(function (candidate) {
    return "".concat(candidate.candidate_type, ":").concat(candidate.id) === selectedCandidateKey;
  });
  var fetchDetail = function fetchDetail(candidate) {
    return getRequest("/projects/".concat(projectResourceId, "/proposal_review_candidate"), {
      candidate_type: candidate.candidate_type,
      candidate_id: candidate.id
    }).then(function (_ref15) {
      var data = _ref15.data;
      setDetail(data);
      setDetailLoading(false);
    })["catch"](function () {
      setDetailLoading(false);
    });
  };
  var selectCandidate = function selectCandidate(candidate) {
    setSelectedCandidate(candidate);
    setMobileDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    var url = new URL(window.location);
    url.searchParams.set('tab', 'proposal-review');
    url.searchParams.set('candidate_type', candidate.candidate_type);
    url.searchParams.set('candidate_id', candidate.id);
    window.history.pushState({}, '', url);
    fetchDetail(candidate);
  };
  var refreshAfterAction = function refreshAfterAction() {
    fetchList();
    if (selectedCandidate) fetchDetail(selectedCandidate);
  };
  var handleWaitlist = function handleWaitlist() {
    if (!selectedCandidate || selectedCandidate.candidate_type !== 'proposal' || !detail) return;
    setDetailLoading(true);
    putRequest("/proposals/".concat(detail.id, "/waitlist"), {}, csrfToken).then(function () {
      setShowWaitlistModal(true);
      refreshAfterAction();
    })["catch"](function () {
      setDetailLoading(false);
    });
  };
  useEffect(function () {
    if (listLoading || candidates.length === 0 || selectedCandidate) return;
    var params = new URLSearchParams(window.location.search);
    var candidateType = params.get('candidate_type');
    var candidateId = params.get('candidate_id');
    var fromUrl = candidates.find(function (candidate) {
      return candidate.candidate_type === candidateType && candidate.id.toString() === candidateId;
    });
    selectCandidate(fromUrl || candidates[0]);
  }, [listLoading, candidates, selectedCandidate]);
  var selectByOffset = function selectByOffset(offset) {
    var nextCandidate = filteredCandidates[selectedIndex + offset];
    if (nextCandidate) selectCandidate(nextCandidate);
  };
  if (listLoading) {
    return /*#__PURE__*/React.createElement("div", {
      className: "g-flex-12 project-info-column proposal-review-loading"
    }, /*#__PURE__*/React.createElement(Loading, null));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "g-flex-12 project-info-column proposal-review-shell"
  }, /*#__PURE__*/React.createElement("div", {
    className: "row proposal-review-layout proposal-review-mobile-detail-".concat(mobileDetailOpen)
  }, /*#__PURE__*/React.createElement(CandidateList, {
    candidates: filteredCandidates,
    allCandidates: candidates,
    selectedCandidateKey: selectedCandidateKey,
    onSelect: selectCandidate,
    employmentType: employmentType,
    query: query,
    setQuery: setQuery,
    status: status,
    setStatus: setStatus
  }), /*#__PURE__*/React.createElement(DetailPanel, {
    detail: detail,
    loading: detailLoading,
    onBack: function onBack() {
      return setMobileDetailOpen(false);
    },
    onPrevious: function onPrevious() {
      return selectByOffset(-1);
    },
    onNext: function onNext() {
      return selectByOffset(1);
    },
    hasPrevious: selectedIndex > 0,
    hasNext: selectedIndex > -1 && selectedIndex < filteredCandidates.length - 1,
    employmentType: employmentType,
    onWaitlist: handleWaitlist,
    onActionComplete: refreshAfterAction,
    csrfToken: csrfToken,
    cloraRep: cloraRep,
    initialTeamMembers: initialTeamMembers,
    invitableRoles: invitableRoles,
    createInterviewUrl: createInterviewUrl,
    availableInterviewSlotUrl: availableInterviewSlotUrl,
    hiddenFeedbackOptions: hiddenFeedbackOptions,
    visibleFeedbackOptions: visibleFeedbackOptions,
    consultantFeedbackOptions: consultantFeedbackOptions,
    projectsOptions: projectsOptions,
    projectResourceId: projectResourceId,
    sourceOptions: sourceOptions,
    recipientOptions: recipientOptions,
    basePermissionsUrl: basePermissionsUrl,
    editableRoles: editableRoles,
    currentUserEmail: currentUserEmail
  })), detail && /*#__PURE__*/React.createElement(WaitlistedNotification, {
    consultantName: ((_detail$header2 = detail.header) === null || _detail$header2 === void 0 ? void 0 : _detail$header2.consultant_name) || 'Candidate',
    handleShowQuestion: function handleShowQuestion() {
      return setShowWaitlistModal(false);
    },
    handleShowCoworkerModal: function handleShowCoworkerModal() {
      return setShowWaitlistModal(false);
    },
    handleCloseModal: function handleCloseModal() {
      return setShowWaitlistModal(false);
    },
    isEmployer: !cloraRep,
    showModal: showWaitlistModal
  }));
}
ProjectProposalReviewTab.propTypes = {
  projectResourceId: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  cloraRep: PropTypes.bool,
  initialTeamMembers: PropTypes.shape({}),
  invitableRoles: PropTypes.arrayOf(PropTypes.string),
  createInterviewUrl: PropTypes.string,
  availableInterviewSlotUrl: PropTypes.string,
  hiddenFeedbackOptions: PropTypes.arrayOf(PropTypes.shape({})),
  visibleFeedbackOptions: PropTypes.arrayOf(PropTypes.shape({})),
  consultantFeedbackOptions: PropTypes.arrayOf(PropTypes.shape({})),
  projectsOptions: PropTypes.arrayOf(PropTypes.shape({})),
  sourceOptions: PropTypes.arrayOf(PropTypes.shape({})),
  recipientOptions: PropTypes.arrayOf(PropTypes.shape({})),
  basePermissionsUrl: PropTypes.string.isRequired,
  editableRoles: PropTypes.arrayOf(PropTypes.string),
  currentUserEmail: PropTypes.string
};
ProjectProposalReviewTab.defaultProps = {
  cloraRep: false,
  initialTeamMembers: {},
  invitableRoles: [],
  createInterviewUrl: '',
  availableInterviewSlotUrl: '',
  hiddenFeedbackOptions: [],
  visibleFeedbackOptions: [],
  consultantFeedbackOptions: [],
  projectsOptions: [],
  sourceOptions: [],
  recipientOptions: [],
  editableRoles: [],
  currentUserEmail: null
};
ChipGroup.propTypes = {
  values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    name: PropTypes.string,
    experience: PropTypes.string,
    ranked: PropTypes.bool
  })])).isRequired
};
ResponseIndicator.propTypes = {
  checked: PropTypes.string
};
ResponseIndicator.defaultProps = {
  checked: 'unanswered'
};
ExpandableText.propTypes = {
  text: PropTypes.string,
  emptyText: PropTypes.string
};
ExpandableText.defaultProps = {
  text: '',
  emptyText: 'No summary provided.'
};
Section.propTypes = {
  title: PropTypes.string.isRequired,
  titleLink: PropTypes.shape({
    href: PropTypes.string,
    label: PropTypes.string
  }),
  children: PropTypes.node,
  headerAside: PropTypes.node
};
Section.defaultProps = {
  titleLink: null,
  children: null,
  headerAside: null
};
ProposalDetailSections.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({}))
};
ProposalDetailSections.defaultProps = {
  sections: []
};
CandidateList.propTypes = {
  candidates: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  allCandidates: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedCandidateKey: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  employmentType: PropTypes.string.isRequired,
  query: PropTypes.string.isRequired,
  setQuery: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  setStatus: PropTypes.func.isRequired
};
CandidateList.defaultProps = {
  selectedCandidateKey: null
};
DetailHeader.propTypes = {
  detail: PropTypes.shape({
    header: PropTypes.shape({}).isRequired,
    primary_actions: PropTypes.arrayOf(PropTypes.string).isRequired,
    secondary_actions: PropTypes.arrayOf(PropTypes.shape({})).isRequired
  }).isRequired,
  onBack: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  hasPrevious: PropTypes.bool.isRequired,
  hasNext: PropTypes.bool.isRequired,
  onWaitlist: PropTypes.func.isRequired,
  onActionComplete: PropTypes.func.isRequired,
  csrfToken: PropTypes.string.isRequired,
  cloraRep: PropTypes.bool,
  initialTeamMembers: PropTypes.shape({}),
  invitableRoles: PropTypes.arrayOf(PropTypes.string),
  createInterviewUrl: PropTypes.string,
  availableInterviewSlotUrl: PropTypes.string,
  hiddenFeedbackOptions: PropTypes.arrayOf(PropTypes.shape({})),
  visibleFeedbackOptions: PropTypes.arrayOf(PropTypes.shape({})),
  consultantFeedbackOptions: PropTypes.arrayOf(PropTypes.shape({})),
  projectsOptions: PropTypes.arrayOf(PropTypes.shape({})),
  projectResourceId: PropTypes.string.isRequired,
  sourceOptions: PropTypes.arrayOf(PropTypes.shape({})),
  recipientOptions: PropTypes.arrayOf(PropTypes.shape({})),
  basePermissionsUrl: PropTypes.string.isRequired,
  editableRoles: PropTypes.arrayOf(PropTypes.string),
  currentUserEmail: PropTypes.string
};
DetailHeader.defaultProps = {
  cloraRep: false,
  initialTeamMembers: {},
  invitableRoles: [],
  createInterviewUrl: '',
  availableInterviewSlotUrl: '',
  hiddenFeedbackOptions: [],
  visibleFeedbackOptions: [],
  consultantFeedbackOptions: [],
  projectsOptions: [],
  sourceOptions: [],
  recipientOptions: [],
  editableRoles: [],
  currentUserEmail: null
};
DetailPanel.propTypes = {
  detail: PropTypes.shape({}),
  loading: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  hasPrevious: PropTypes.bool.isRequired,
  hasNext: PropTypes.bool.isRequired,
  employmentType: PropTypes.string.isRequired,
  onWaitlist: PropTypes.func.isRequired,
  onActionComplete: PropTypes.func.isRequired,
  csrfToken: PropTypes.string.isRequired,
  cloraRep: PropTypes.bool,
  initialTeamMembers: PropTypes.shape({}),
  invitableRoles: PropTypes.arrayOf(PropTypes.string),
  createInterviewUrl: PropTypes.string,
  availableInterviewSlotUrl: PropTypes.string,
  hiddenFeedbackOptions: PropTypes.arrayOf(PropTypes.shape({})),
  visibleFeedbackOptions: PropTypes.arrayOf(PropTypes.shape({})),
  consultantFeedbackOptions: PropTypes.arrayOf(PropTypes.shape({})),
  projectsOptions: PropTypes.arrayOf(PropTypes.shape({})),
  projectResourceId: PropTypes.string.isRequired,
  sourceOptions: PropTypes.arrayOf(PropTypes.shape({})),
  recipientOptions: PropTypes.arrayOf(PropTypes.shape({})),
  basePermissionsUrl: PropTypes.string.isRequired,
  editableRoles: PropTypes.arrayOf(PropTypes.string),
  currentUserEmail: PropTypes.string
};
DetailPanel.defaultProps = {
  detail: null,
  cloraRep: false,
  initialTeamMembers: {},
  invitableRoles: [],
  createInterviewUrl: '',
  availableInterviewSlotUrl: '',
  hiddenFeedbackOptions: [],
  visibleFeedbackOptions: [],
  consultantFeedbackOptions: [],
  projectsOptions: [],
  sourceOptions: [],
  recipientOptions: [],
  editableRoles: [],
  currentUserEmail: null
};
export default ProjectProposalReviewTab;